import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import {
	animationFrames,
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	EMPTY,
	filter,
	from,
	map,
	merge,
	of,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs'
import '../button/icon-button'
import '../link/link'
import '../tone-dot/tone-dot'
import '../typography/typography'

/** Bars drawn when the note's own peaks are decoded from its audio. */
const DECODED_BARS = 40

/** The note playing now, so starting one pauses whichever was playing. */
const nowPlaying$ = new BehaviorSubject<SchmancyVoiceNote | null>(null)

const clock = (seconds: number): string => {
	const whole = Math.max(0, Math.round(seconds))
	return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

/**
 * A recorded voice message as a chat shows it: play and pause, the note's
 * waveform as static bars that fill as it plays, its length, an optional mark
 * that the listener has not played it yet, and an optional link to its
 * transcript.
 *
 * Only one note plays at a time across the page. The bars come from `peaks`
 * when the consumer has them (a recorder's own levels), and are otherwise
 * decoded once from `src`.
 *
 * @fires played - The first time this note starts playing. The consumer clears
 *   its own unplayed state from here; the mark leaves on its own.
 * @fires transcript - The transcript link was pressed. `detail.transcript`
 *   carries the text, for the consumer to show where it shows details.
 * @fires remove - The remove control was pressed. Only a `removable` note has
 *   one; the consumer drops the note from its draft.
 */
@schmancyElement('schmancy-voice-note')
export class SchmancyVoiceNote extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				width: 15rem;
				max-width: 100%;
				min-width: 0;
			}
		`,
	]

	/** The audio to play. */
	@property({ type: String }) src = ''

	/** Levels from 0 to 1, one bar each. Empty decodes them from `src`. */
	@property({ type: Array }) peaks: number[] = []

	/** Length in seconds, when known before the audio loads. Otherwise read from it. */
	@property({ type: Number }) duration = 0

	/** The listener has not played this note yet: a dot beside its length. */
	@property({ type: Boolean, reflect: true }) unplayed = false

	/** The note's words. Non-empty shows a Transcript link that fires `transcript`. */
	@property({ type: String }) transcript = ''

	/** A note still in a draft: a remove control at its end fires `remove`. */
	@property({ type: Boolean, reflect: true }) removable = false

	@state() private playing = false
	@state() private position = 0
	@state() private measured = 0
	@state() private decoded: number[] = []
	@state() private failed = false

	private audio = createRef<HTMLAudioElement>()
	private readonly src$ = new BehaviorSubject('')
	private readonly playing$ = new BehaviorSubject(false)

	override connectedCallback(): void {
		super.connectedCallback()
		const decode$ = this.src$.pipe(
			distinctUntilChanged(),
			filter(src => this.peaks.length === 0 && src !== ''),
			switchMap(src =>
				from(fetch(src).then(response => response.arrayBuffer())).pipe(
					switchMap(buffer => {
						const context = new AudioContext()
						return from(context.decodeAudioData(buffer).finally(() => context.close()))
					}),
					map(audio => {
						const samples = audio.getChannelData(0)
						const step = Math.max(1, Math.floor(samples.length / DECODED_BARS))
						const levels = Array.from({ length: DECODED_BARS }, (_, bar) => {
							let peak = 0
							for (let i = bar * step; i < Math.min(samples.length, (bar + 1) * step); i++)
								peak = Math.max(peak, Math.abs(samples[i]))
							return peak
						})
						const loudest = Math.max(...levels, 0.0001)
						return levels.map(level => level / loudest)
					}),
					// A note that cannot be decoded keeps its flat bars and still plays.
					catchError(() => of([] as number[])),
				),
			),
			tap(levels => {
				this.decoded = levels
			}),
		)
		// While playing, the bars follow every frame rather than the audio's own
		// coarse timeupdate.
		const progress$ = this.playing$.pipe(
			distinctUntilChanged(),
			switchMap(playing => (playing ? animationFrames() : EMPTY)),
			tap(() => {
				this.position = this.audio.value?.currentTime ?? 0
			}),
		)
		const others$ = nowPlaying$.pipe(
			filter(note => note !== this && this.playing),
			tap(() => this.audio.value?.pause()),
		)
		merge(decode$, progress$, others$).pipe(takeUntil(this.disconnecting)).subscribe()
	}

	override disconnectedCallback(): void {
		this.audio.value?.pause()
		if (nowPlaying$.value === this) nowPlaying$.next(null)
		super.disconnectedCallback()
	}

	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (changed.has('src')) {
			this.failed = false
			this.src$.next(this.src)
		}
		if (changed.has('playing')) this.playing$.next(this.playing)
	}

	private toggle(): void {
		const audio = this.audio.value
		if (!audio) return
		if (this.playing) {
			audio.pause()
			return
		}
		if (audio.error) audio.load()
		this.failed = false
		const source = this.src
		audio.play().catch(() => {
			if (this.src !== source) return
			this.failed = true
			this.playing = false
		})
	}

	render() {
		const length = this.duration || this.measured
		const levels = this.peaks.length > 0 ? this.peaks : this.decoded.length > 0 ? this.decoded : Array(DECODED_BARS).fill(0.15)
		const filled = length > 0 ? this.position / length : 0
		const started = this.playing || this.position > 0
		return html`
			<div class="flex min-w-0 items-center gap-2" role="group" aria-label=${componentText('voiceNote.name', { length: clock(length) })}>
				<audio
					${ref(this.audio)}
					src=${this.src}
					preload="metadata"
					@loadedmetadata=${(e: Event) => {
						const seconds = (e.target as HTMLAudioElement).duration
						if (Number.isFinite(seconds)) this.measured = seconds
					}}
					@playing=${() => {
						this.playing = true
						nowPlaying$.next(this)
						if (this.unplayed) {
							this.unplayed = false
							this.dispatchEvent(new CustomEvent('played', { bubbles: true, composed: true }))
						}
					}}
					@pause=${() => {
						this.playing = false
						this.position = this.audio.value?.currentTime ?? 0
					}}
					@ended=${() => {
						this.playing = false
						this.position = 0
						if (this.audio.value) this.audio.value.currentTime = 0
					}}
					@error=${() => {
						this.failed = true
						this.playing = false
					}}
				></audio>
				<schmancy-icon-button
					variant="filled"
					size="sm"
					icon=${this.playing ? 'pause' : 'play_arrow'}
					aria-label=${componentText(this.playing ? 'voiceNote.pause' : 'voiceNote.play')}
					@click=${() => this.toggle()}
				></schmancy-icon-button>
				<div class="grid min-w-0 flex-1 gap-0.5">
					<div class="flex h-6 min-w-0 items-center gap-0.5 overflow-hidden" aria-hidden="true">
						${repeat(
							levels,
							(_, i) => i,
							(level, i) =>
								html`<span
									class=${this.classMap({
										'min-w-0 flex-1 rounded-full bg-current h-(--level)': true,
										'opacity-40': i / levels.length >= filled,
									})}
									style="--level: ${Math.max(3, Math.round(level * 22))}px"
								></span>`,
						)}
					</div>
					<div class="flex min-w-0 items-center gap-2">
						<schmancy-typography type="label" token="xs" class="tabular-nums">${clock(started ? this.position : length)}</schmancy-typography>
						${when(
							this.unplayed,
							() => html`<schmancy-tone-dot color="info" class="w-2 h-2" label=${componentText('voiceNote.unplayed')}></schmancy-tone-dot>`,
						)}
						${when(
							this.transcript !== '',
							() => html`<schmancy-typography type="label" token="xs" class="ml-auto"
								><schmancy-link
									standalone
									@press=${() =>
										this.dispatchEvent(
											new CustomEvent('transcript', { detail: { transcript: this.transcript }, bubbles: true, composed: true }),
										)}
									>${componentText('voiceNote.transcript')}</schmancy-link
								></schmancy-typography
							>`,
						)}
					</div>
				</div>
				${when(
					this.removable,
					() => html`<schmancy-icon-button
						size="sm"
						icon="delete"
						aria-label=${componentText('voiceNote.remove', { length: clock(length) })}
						@click=${() => {
							this.audio.value?.pause()
							this.dispatchEvent(new CustomEvent('remove', { bubbles: true, composed: true }))
						}}
					></schmancy-icon-button>`,
				)}
			</div>
			${when(this.failed, () => html`<schmancy-typography type="label" token="sm" role="alert" class="block pt-1 text-error-default">${componentText('voiceNote.failed')}</schmancy-typography>`)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-voice-note': SchmancyVoiceNote
	}
}
